import { useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, Upload, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const DISPUTE_TYPES = [
  { value: "", label: "Select type" },
  { value: "Item Damaged", label: "Item Damaged" },
  { value: "Item Not As Described", label: "Item Not As Described" },
  { value: "Late Return", label: "Late Return" },
  { value: "Deposit Disagreement", label: "Deposit Disagreement" },
  { value: "Communication Issue", label: "Communication Issue" },
  { value: "Other", label: "Other" },
];

const RESOLUTIONS = [
  { value: "", label: "Select desired resolution" },
  { value: "Full Refund", label: "Full Refund" },
  { value: "Partial Refund", label: "Partial Refund" },
  { value: "Deposit Release", label: "Deposit Release" },
  { value: "Deposit Claim", label: "Deposit Claim" },
  { value: "Other", label: "Other" },
];

function DisputeForm() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    booking_id: "", role: "", dispute_type: "", description: "",
    desired_resolution: "", additional_notes: "",
  });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files);
    const valid = selected.filter(f => f.size <= 5 * 1024 * 1024 && (f.type.startsWith("image/") || f.type === "application/pdf"));
    if (valid.length + files.length > 3) {
      toast.error("Maximum 3 files allowed.");
      return;
    }
    setFiles((prev) => [...prev, ...valid].slice(0, 3));
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.description.length < 50) {
      toast.error("Please provide at least 50 characters describing the issue.");
      return;
    }
    setSending(true);
    try {
      await api.post("/disputes", {
        booking_id: form.booking_id,
        role: form.role,
        dispute_type: form.dispute_type,
        description: form.description,
        desired_resolution: form.desired_resolution,
        additional_notes: form.additional_notes,
      });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again or email us at support@paradiseborrowed.com.");
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="dispute-login-prompt">
        <LogIn className="w-10 h-10 text-stone-300 mx-auto mb-4" />
        <p className="text-stone-700 font-medium mb-2 font-ui">Log in to submit a dispute.</p>
        <Link to="/auth">
          <Button className="rounded-full px-8 bg-coral hover:bg-[#d44814] text-white mt-3 font-ui" data-testid="dispute-login-btn">Log In</Button>
        </Link>
        <p className="text-stone-400 text-sm mt-6 font-ui">
          Or email us directly at <a href="mailto:support@paradiseborrowed.com" className="text-coral hover:underline">support@paradiseborrowed.com</a>
        </p>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="dispute-success">
        <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
          <Send className="w-6 h-6" />
        </div>
        <h3 className="text-xl text-stone-900 font-medium mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Dispute submitted</h3>
        <p className="text-stone-500 text-sm font-ui">Our team will review it within 48 hours. You'll receive a confirmation email shortly.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-8" data-testid="dispute-form-card">
      {/* Read-only user info */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6 pb-6 border-b border-stone-100">
        <div>
          <label className="block text-xs font-medium text-stone-400 mb-1.5 font-ui">Your name</label>
          <div className="px-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-lg text-stone-700 font-ui">{user.name}</div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-400 mb-1.5 font-ui">Your email</label>
          <div className="px-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-lg text-stone-700 font-ui">{user.email}</div>
        </div>
      </div>

      <form onSubmit={handleSubmit} data-testid="dispute-form">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Booking ID *</label>
            <input type="text" required value={form.booking_id}
              onChange={(e) => setForm({ ...form, booking_id: e.target.value })}
              className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral bg-white font-ui"
              placeholder="e.g. abc123-def456"
              data-testid="dispute-booking-id" />
            <p className="text-xs text-stone-400 mt-1 font-ui">Find this in your booking confirmation or dashboard.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">I am the... *</label>
            <div className="flex gap-4 mt-2">
              {["lender", "borrower"].map((r) => (
                <label key={r} className="flex items-center gap-2 cursor-pointer font-ui text-sm text-stone-700">
                  <input type="radio" name="role" value={r} required checked={form.role === r}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="accent-coral" data-testid={`dispute-role-${r}`} />
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Dispute Type *</label>
            <select required value={form.dispute_type}
              onChange={(e) => setForm({ ...form, dispute_type: e.target.value })}
              className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral bg-white font-ui"
              data-testid="dispute-type">
              {DISPUTE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Desired Resolution *</label>
            <select required value={form.desired_resolution}
              onChange={(e) => setForm({ ...form, desired_resolution: e.target.value })}
              className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral bg-white font-ui"
              data-testid="dispute-resolution">
              {RESOLUTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">What happened? *</label>
          <textarea required rows="5" minLength={50} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Please describe the issue clearly. Include dates, item details, and any relevant context."
            className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral resize-none bg-white font-ui"
            data-testid="dispute-description" />
          <p className="text-xs text-stone-400 mt-1 font-ui">Minimum 50 characters</p>
        </div>

        {/* File upload */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Upload Evidence (optional)</label>
          <div className="border-2 border-dashed border-stone-200 rounded-lg p-4 text-center hover:border-coral/40 transition-colors">
            <Upload className="w-5 h-5 text-stone-400 mx-auto mb-2" />
            <label className="cursor-pointer text-sm text-coral font-medium font-ui hover:underline">
              Choose files
              <input type="file" multiple accept="image/*,.pdf" onChange={handleFiles} className="hidden" data-testid="dispute-file-input" />
            </label>
            <p className="text-xs text-stone-400 mt-1 font-ui">Images or PDFs, max 3 files, 5MB each</p>
          </div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-1.5 text-xs text-stone-600 font-ui">
                  {f.name.length > 20 ? f.name.slice(0, 17) + "..." : f.name}
                  <button type="button" onClick={() => removeFile(i)} className="text-stone-400 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Additional Notes (optional)</label>
          <textarea rows="3" value={form.additional_notes}
            onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
            placeholder="Anything else we should know?"
            className="w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral resize-none bg-white font-ui"
            data-testid="dispute-notes" />
        </div>

        <Button type="submit" disabled={sending}
          className="w-full py-3 rounded-lg bg-coral hover:bg-[#d44814] text-white font-medium text-sm font-ui"
          data-testid="dispute-submit">
          {sending ? "Submitting..." : "Submit Dispute"}
        </Button>
      </form>
    </div>
  );
}

export default function DisputeResolution() {
  const seoData = {
    title: "Dispute Resolution Policy | Paradise Borrowed",
    description: "Learn how Paradise Borrowed handles disputes between lenders and borrowers. Our fair mediation process and binding arbitration terms.",
    url: "https://www.paradiseborrowed.com/trust"
  };

  return (
    <>
      <SEOHead title={seoData.title} description={seoData.description} canonicalUrl={seoData.url} type="website" schema={generateWebPageSchema(seoData)} />
      <div className="bg-[#FAFAF8]" data-testid="dispute-resolution-page">

        {/* Hero */}
        <div className="max-w-4xl mx-auto px-4 pt-16 pb-12 text-center">
          <p className="text-xs tracking-widest uppercase text-stone-400 mb-4 font-ui">Dispute Resolution</p>
          <h1 className="text-4xl sm:text-5xl text-stone-900 mb-4" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>
            Dispute Resolution Policy
          </h1>
          <p className="text-stone-500 max-w-xl mx-auto leading-relaxed font-ui">
            Paradise Borrowed, Inc. — Last Updated: March 15, 2026
          </p>
        </div>

        {/* Policy Content */}
        <div className="max-w-3xl mx-auto px-4 pb-8 space-y-6">

          <PolicySection num="1" title="Overview">
            <p>Paradise Borrowed, Inc. ("Paradise Borrowed," "we," "us," or "the Platform") is a Delaware corporation operating as a neutral peer-to-peer marketplace. Paradise Borrowed is not a party to any borrowing agreement between lenders and borrowers ("Users") and does not own, control, or inspect any items listed on the Platform. However, we are committed to providing a fair, structured, and transparent process for resolving disputes that arise from transactions conducted through the Platform.</p>
          </PolicySection>

          <PolicySection num="2" title="Scope">
            <p className="mb-3">This Dispute Resolution Policy applies to all claims arising from or related to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Alleged damage to a listed item during a borrowing period</li>
              <li>Late or non-return of a borrowed item</li>
              <li>Item condition discrepancies (item not as described)</li>
              <li>Unauthorized charges or payment disputes</li>
              <li>Cancellation disagreements</li>
              <li>Any other claim between a lender and borrower arising from a Platform transaction</li>
            </ul>
          </PolicySection>

          <PolicySection num="3" title="Good Faith Requirement">
            <p>Before initiating a formal dispute, Users are required to make a good faith attempt to resolve the issue directly through the Platform's messaging system. Paradise Borrowed's in-app messaging is timestamped and logged, and communication records may be used as evidence in any subsequent dispute review. Users who fail to attempt direct resolution may have their dispute eligibility limited at our discretion.</p>
          </PolicySection>

          <PolicySection num="4" title="Formal Dispute Filing">
            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">4.1 Filing Window</h3>
            <p>Either party may open a formal dispute within 48 hours of the confirmed return of the item as recorded by the Platform. Disputes submitted outside this window will not be eligible for Platform mediation and will be considered waived, except in cases of fraud or concealed damage.</p>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">4.2 How to File</h3>
            <p className="mb-3">Disputes must be submitted through the Platform's designated Dispute Center. The filing party must provide:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>A clear written description of the issue</li>
              <li>Photographic or video evidence where applicable</li>
              <li>Relevant transaction details (booking ID, dates, item listing)</li>
              <li>Any prior in-app communication related to the dispute</li>
            </ul>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">4.3 Notification</h3>
            <p>Upon submission, the responding party will be notified immediately and given 48 hours to submit their response and supporting evidence. Failure to respond within this window may result in a default finding in favor of the filing party.</p>
          </PolicySection>

          <PolicySection num="5" title="Platform Mediation Process">
            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">5.1 Review</h3>
            <p>Paradise Borrowed's Trust & Safety team will review all submitted evidence, including but not limited to: photographs, in-app messages, transaction timestamps, item condition disclosures made at listing, and any applicable Platform activity logs.</p>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">5.2 Resolution Timeline</h3>
            <p>We will issue a written determination within 5 business days of receiving complete submissions from both parties. Complex cases may require up to 10 business days; Users will be notified if an extension is necessary.</p>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">5.3 Determinations</h3>
            <p className="mb-3">Platform determinations may include, but are not limited to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Full or partial refund to the borrower</li>
              <li>Account warnings, suspensions, or permanent removal from the Platform</li>
              <li>Documentation of findings in the User's account record</li>
            </ul>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">5.4 Finality of Platform Determination</h3>
            <p>Paradise Borrowed's internal determination is final with respect to Platform-controlled actions (account standing, refunds). It does not constitute a legal judgment and does not prevent either party from pursuing independent legal remedies as described in Section 7.</p>
          </PolicySection>

          <PolicySection num="6" title="Limitations of Platform Liability">
            <p>Paradise Borrowed acts solely as a neutral mediator in the dispute process. We do not guarantee the accuracy of evidence submitted by either party and are not liable for any losses, damages, or outcomes arising from a dispute determination made in good faith. Our maximum liability in connection with any dispute mediation is limited to the transaction value of the booking at issue.</p>
          </PolicySection>

          <PolicySection num="7" title="Escalation — Binding Arbitration">
            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">7.1 Informal Escalation</h3>
            <p>If either party is unsatisfied with the Platform's internal determination, or if a dispute falls outside the scope of Platform mediation, the dissatisfied party must first submit a written notice of escalation to Paradise Borrowed at <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a> describing the nature of the unresolved claim. Paradise Borrowed will have 15 days to respond.</p>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">7.2 Binding Arbitration</h3>
            <p>If the dispute remains unresolved after informal escalation, it shall be submitted to binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, or alternatively JAMS, at Paradise Borrowed's election. Arbitration shall be conducted in Wilmington, Delaware, or via videoconference at either party's request. The arbitrator's decision shall be final and binding and may be entered as a judgment in any court of competent jurisdiction.</p>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">7.3 Small Claims Carve-Out</h3>
            <p>Notwithstanding the above, either party may bring an individual claim in small claims court in their jurisdiction if the claim qualifies under that court's jurisdictional limits, without first proceeding through arbitration.</p>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">7.4 Class Action Waiver</h3>
            <p>All disputes must be brought in the parties' individual capacities. Users waive the right to bring or participate in any class action, collective action, or representative proceeding against Paradise Borrowed.</p>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">7.5 Governing Law</h3>
            <p>This Policy and any disputes arising hereunder shall be governed by the laws of the State of Delaware, without regard to its conflict of law provisions.</p>
          </PolicySection>

          <PolicySection num="8" title="Fraudulent Conduct & Willful Misconduct">
            <p className="mb-3">Paradise Borrowed maintains a zero-tolerance policy toward fraudulent conduct, willful misconduct, and deliberate dishonesty in connection with any booking transaction or dispute process.</p>
            <p className="mb-3">The following conduct may result in immediate and permanent account termination, without appeal:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Intentional or willful destruction, damage, or theft of a borrowed item</li>
              <li>Deliberate misrepresentation of an item's condition by a lender prior to or following a booking</li>
              <li>Submission of falsified, staged, or manipulated evidence (including photographs, videos, or written statements) during the dispute process</li>
              <li>Making materially false claims for the purpose of securing a refund or causing financial harm to another User</li>
              <li>Colluding with a third party to fabricate or distort the facts of a dispute</li>
              <li>Any conduct determined by Paradise Borrowed, in its sole and reasonable discretion, to constitute bad faith, fraud, or intentional harm to another User or to the Platform</li>
            </ul>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">Permanent Platform Ban</h3>
            <p className="mb-3">Users found to have engaged in any of the above conduct will be permanently banned from Paradise Borrowed across all accounts, devices, and identities. Paradise Borrowed reserves the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Permanently terminate the User's account and revoke access to the Platform</li>
              <li>Refuse any future account registration by the same individual, including attempts made under a different name, email address, or identity</li>
              <li>Withhold or redirect any funds associated with the transaction pending investigation</li>
              <li>Report the conduct to relevant law enforcement authorities where criminal activity is suspected, including but not limited to theft, fraud, or destruction of property</li>
              <li>Pursue or support independent civil legal action against the offending party on behalf of the harmed User</li>
            </ul>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">Effect on Dispute Determination</h3>
            <p>Where evidence of willful misconduct or fraud is identified during the dispute review process, Paradise Borrowed reserves the right to issue an immediate determination adverse to the offending party, regardless of the stage of review.</p>

            <h3 className="font-semibold text-base text-stone-800 mt-4 mb-2">Reporting Misconduct</h3>
            <p>Users who have reason to believe the opposing party has engaged in fraudulent or willful misconduct are encouraged to flag this explicitly in their dispute submission and provide all available supporting evidence. Paradise Borrowed treats all misconduct reports with strict confidentiality.</p>
          </PolicySection>

          <PolicySection num="9" title="Abuse of Dispute Process">
            <p>Users who file disputes in bad faith, submit fraudulent evidence, or repeatedly abuse the dispute system may be subject to immediate account suspension, permanent removal from the Platform, and/or referral to appropriate legal authorities.</p>
          </PolicySection>

          <PolicySection num="10" title="Amendments">
            <p>Paradise Borrowed reserves the right to modify this Dispute Resolution Policy at any time. Material changes will be communicated to Users via email or in-app notification. Continued use of the Platform following notice of changes constitutes acceptance of the revised Policy.</p>
          </PolicySection>

          <PolicySection num="11" title="Contact">
            <p className="mb-3">For dispute-related inquiries:</p>
            <div>
              <p className="font-semibold">Trust & Safety Team</p>
              <p className="font-semibold">Paradise Borrowed, Inc.</p>
              <p>Email: <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a></p>
              <p>Website: <a href="https://www.paradiseborrowed.com" className="text-coral hover:underline">www.paradiseborrowed.com</a></p>
            </div>
          </PolicySection>

          {/* Related links */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 flex gap-6">
            <Link to="/terms-of-service" className="text-coral hover:underline text-sm font-ui">Terms of Service</Link>
            <Link to="/privacy-policy" className="text-coral hover:underline text-sm font-ui">Privacy Policy</Link>
            <Link to="/trust-and-safety" className="text-coral hover:underline text-sm font-ui">Trust & Safety</Link>
          </div>
        </div>

        {/* Dispute Intake Form */}
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <p className="text-xs tracking-widest uppercase text-stone-400 mb-3 font-ui">File a dispute</p>
            <h2 className="text-3xl text-stone-900 mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>Submit a Dispute</h2>
            <p className="text-stone-500 text-sm font-ui max-w-lg mx-auto">
              Need to resolve an issue with a booking? Use the form below to submit a dispute. Our team reviews all disputes within 48 hours. Please have your Booking ID ready and provide as much detail as possible so we can help quickly.
            </p>
          </div>
          <DisputeForm />
        </div>

      </div>
    </>
  );
}

function PolicySection({ num, title, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-8">
      <h2 className="text-xl text-stone-900 mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
        {num}. {title}
      </h2>
      <div className="text-stone-600 text-sm leading-relaxed font-ui space-y-2">
        {children}
      </div>
    </div>
  );
}
